/**
 * Curated pharmacy directory data with estimated pricing.
 * Pharmacy details (names, phones, websites, addresses, services) reflect
 * real publicly available information. Pricing is estimated and will be
 * replaced by real-time API data in a future iteration.
 */

const IS_DEV_BUILD = typeof __DEV__ !== "undefined" && __DEV__;

export const ENABLE_PHARMACY_MOCK_DATA =
  IS_DEV_BUILD || process.env.EXPO_PUBLIC_DEV_LOCAL_MOCK === "true";

/* ─── Prescribed Medications ─────────────────────────────────────── */

export type CoverageStatus = "covered" | "pa_approved" | "cash_pay";
export type MedForm = "pen" | "vial" | "tablet" | "suppository" | "syringe";
export type MedRoute = "SubQ" | "IM" | "Oral" | "Vaginal" | "Topical";

export interface PrescribedMed {
  id: string;
  name: string;
  genericName: string;
  dosage: string;
  form: MedForm;
  route: MedRoute;
  coverage: CoverageStatus;
  manufacturer: string;
  refillsRemaining: number;
}

export const MOCK_PRESCRIBED_MEDS: PrescribedMed[] = [
  {
    id: "med-gonal-f",
    name: "Gonal-F",
    genericName: "Follitropin alfa",
    dosage: "900 IU",
    form: "pen",
    route: "SubQ",
    coverage: "covered",
    manufacturer: "EMD Serono",
    refillsRemaining: 2,
  },
  {
    id: "med-cetrotide",
    name: "Cetrotide",
    genericName: "Cetrorelix acetate",
    dosage: "0.25 mg",
    form: "syringe",
    route: "SubQ",
    coverage: "covered",
    manufacturer: "EMD Serono",
    refillsRemaining: 0,
  },
  {
    id: "med-ovidrel",
    name: "Ovidrel",
    genericName: "Choriogonadotropin alfa",
    dosage: "250 mcg",
    form: "syringe",
    route: "SubQ",
    coverage: "covered",
    manufacturer: "EMD Serono",
    refillsRemaining: 0,
  },
  {
    id: "med-progesterone",
    name: "Endometrin",
    genericName: "Progesterone",
    dosage: "100 mg",
    form: "suppository",
    route: "Vaginal",
    coverage: "cash_pay",
    manufacturer: "Ferring",
    refillsRemaining: 3,
  },
  {
    id: "med-dexamethasone",
    name: "Dexamethasone",
    genericName: "Dexamethasone",
    dosage: "0.5 mg",
    form: "tablet",
    route: "Oral",
    coverage: "covered",
    manufacturer: "Generic",
    refillsRemaining: 5,
  },
];

/* ─── Pharmacies ─────────────────────────────────────────────────── */

export type PharmacyType = "specialty" | "mail_order" | "retail";

export interface PharmacyService {
  label: string;
  available: boolean;
}

export interface PharmacyReview {
  id: string;
  author: string;
  rating: number;
  date: string;
  text: string;
}

export interface Pharmacy {
  id: string;
  name: string;
  type: PharmacyType;
  rating: number;
  reviewCount: number;
  acceptsInsurance: boolean;
  delivery: string;
  shippingCost: number;
  totalPrice: number | null;
  badge?: string;
  services: PharmacyService[];
  reviews: PharmacyReview[];
  phone: string;
  website: string;
  address: string;
}

export const MOCK_PHARMACIES: Pharmacy[] = [
  {
    id: "pharmacy-alto",
    name: "Alto Pharmacy",
    type: "specialty",
    rating: 4.8,
    reviewCount: 1247,
    acceptsInsurance: true,
    delivery: "Free same-day delivery",
    shippingCost: 0,
    totalPrice: null,
    badge: "Insurance preferred",
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "In-app injection training", available: true },
      { label: "24/7 pharmacist chat", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: true },
    ],
    reviews: [],
    phone: "(415) 941-4286",
    website: "alto.com",
    address: "San Francisco, CA",
  },
  {
    id: "pharmacy-mdr",
    name: "MDR Pharmacy",
    type: "specialty",
    rating: 4.7,
    reviewCount: 892,
    acceptsInsurance: false,
    delivery: "Free next-day nationwide, same-day in CA",
    shippingCost: 0,
    totalPrice: 1955,
    badge: "Best cash price",
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Nurse injection training", available: true },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: false },
      { label: "Same-day delivery (CA)", available: true },
    ],
    reviews: [],
    phone: "(800) 515-3784",
    website: "mdrusa.com",
    address: "Encino, CA",
  },
  {
    id: "pharmacy-freedom",
    name: "Freedom Fertility",
    type: "specialty",
    rating: 4.6,
    reviewCount: 2103,
    acceptsInsurance: true,
    delivery: "Free overnight shipping",
    shippingCost: 0,
    totalPrice: 707,
    badge: "TrumpRx pricing",
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "24/7 fertility specialists", available: true },
      { label: "Overnight shipping", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: false },
    ],
    reviews: [],
    phone: "(800) 660-4283",
    website: "freedomfertility.com",
    address: "Byfield, MA",
  },
  {
    id: "pharmacy-encompass",
    name: "Encompass Rx",
    type: "mail_order",
    rating: 4.5,
    reviewCount: 634,
    acceptsInsurance: true,
    delivery: "Free 3-day shipping",
    shippingCost: 0,
    totalPrice: null,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Injection training", available: false },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: false },
    ],
    reviews: [],
    phone: "(800) 990-0379",
    website: "encompassrx.com",
    address: "Multiple locations",
  },
  {
    id: "pharmacy-village",
    name: "VFP Pharmacy Group",
    type: "specialty",
    rating: 4.9,
    reviewCount: 412,
    acceptsInsurance: false,
    delivery: "Free next-day delivery",
    shippingCost: 0,
    totalPrice: 707,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Injection training", available: true },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: true },
    ],
    reviews: [],
    phone: "(866) 266-0868",
    website: "vfppharmacygroup.com",
    address: "Waltham, MA / Chicago, IL / Phoenix, AZ / Los Angeles, CA",
  },
  {
    id: "pharmacy-mandells",
    name: "Mandell's Pharmacy",
    type: "specialty",
    rating: 4.4,
    reviewCount: 318,
    acceptsInsurance: true,
    delivery: "2-day shipping",
    shippingCost: 10,
    totalPrice: 1956,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Injection training", available: true },
      { label: "24/7 pharmacist on-call", available: true },
      { label: "Automatic discount application", available: true },
      { label: "Same-day delivery", available: false },
    ],
    reviews: [],
    phone: "(209) 524-2436",
    website: "mymandellspharmacy.com",
    address: "Modesto, CA",
  },
  {
    id: "pharmacy-smp",
    name: "SMP Pharmacy",
    type: "specialty",
    rating: 4.3,
    reviewCount: 245,
    acceptsInsurance: true,
    delivery: "Free 2-day shipping",
    shippingCost: 0,
    totalPrice: null,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Injection training", available: true },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: false },
    ],
    reviews: [],
    phone: "(800) 539-3411",
    website: "smppharmacy.com",
    address: "Multiple locations",
  },
  {
    id: "pharmacy-cvs",
    name: "CVS Specialty",
    type: "retail",
    rating: 4.2,
    reviewCount: 1893,
    acceptsInsurance: true,
    delivery: "In-store pickup or 3-day shipping",
    shippingCost: 0,
    totalPrice: 2040,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Injection training", available: false },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: true },
      { label: "Same-day delivery", available: false },
    ],
    reviews: [],
    phone: "(800) 237-2767",
    website: "cvs.com/specialty",
    address: "Multiple locations",
  },
  {
    id: "pharmacy-walgreens",
    name: "Walgreens Specialty",
    type: "retail",
    rating: 4.1,
    reviewCount: 1456,
    acceptsInsurance: true,
    delivery: "In-store pickup or overnight shipping",
    shippingCost: 0,
    totalPrice: 1956,
    services: [
      { label: "Cold chain shipping", available: true },
      { label: "Interactive injection training tools", available: true },
      { label: "24/7 pharmacist", available: true },
      { label: "Auto-refill", available: true },
      { label: "Overnight delivery", available: true },
    ],
    reviews: [],
    phone: "(888) 347-3416",
    website: "walgreens.com/specialty",
    address: "Multiple locations",
  },
];

/* ─── Price Matrix (per med per pharmacy) ────────────────────────── */
/* Prices are self-pay / cash-pay in USD.
 * null = no public pricing — user should contact the pharmacy.
 * Sources:
 *   GoodRx (goodrx.com) — coupon prices for retail/mail-order pharmacies
 *   TrumpRx (trumprx.gov) — EMD Serono discounts at Freedom & VFP
 *   MDR — known price-match leader, estimated from public forums
 *   Alto — does not publish; shows "Contact for pricing"
 *   Mandell's / SMP / Encompass — estimated from GoodRx coupon ranges
 */

export const PRICE_MATRIX: Record<string, Record<string, number | null>> = {
  "pharmacy-alto": {
    // Alto does not publish self-pay pricing; provides personalized quotes
    "med-gonal-f": null,
    "med-cetrotide": null,
    "med-ovidrel": null,
    "med-progesterone": null,
    "med-dexamethasone": null,
  },
  "pharmacy-mdr": {
    // MDR is known for best cash prices; price-matches any advertised price
    "med-gonal-f": 1450,
    "med-cetrotide": 302,
    "med-ovidrel": 107,
    "med-progesterone": 91,
    "med-dexamethasone": 5,
  },
  "pharmacy-freedom": {
    // Freedom is a TrumpRx participating pharmacy — TrumpRx prices shown
    "med-gonal-f": 504, // TrumpRx price (was ~$2,898)
    "med-cetrotide": 23, // TrumpRx price (was ~$316)
    "med-ovidrel": 84, // TrumpRx price (was ~$252)
    "med-progesterone": 91, // GoodRx coupon
    "med-dexamethasone": 5,
  },
  "pharmacy-encompass": {
    "med-gonal-f": null,
    "med-cetrotide": null,
    "med-ovidrel": null,
    "med-progesterone": null,
    "med-dexamethasone": null,
  },
  "pharmacy-village": {
    // VFP is a TrumpRx participating pharmacy — TrumpRx prices shown
    "med-gonal-f": 504, // TrumpRx price
    "med-cetrotide": 23, // TrumpRx price
    "med-ovidrel": 84, // TrumpRx price
    "med-progesterone": 91, // GoodRx coupon range
    "med-dexamethasone": 5,
  },
  "pharmacy-mandells": {
    // Mandell's applies discounts automatically; estimated from GoodRx range
    "med-gonal-f": 1451,
    "med-cetrotide": 302,
    "med-ovidrel": 107,
    "med-progesterone": 91,
    "med-dexamethasone": 5,
  },
  "pharmacy-smp": {
    "med-gonal-f": null,
    "med-cetrotide": null,
    "med-ovidrel": null,
    "med-progesterone": null,
    "med-dexamethasone": null,
  },
  "pharmacy-cvs": {
    // CVS retail — GoodRx coupon prices
    "med-gonal-f": 1451,
    "med-cetrotide": 302,
    "med-ovidrel": 107,
    "med-progesterone": 175,
    "med-dexamethasone": 5,
  },
  "pharmacy-walgreens": {
    // Walgreens — GoodRx coupon prices
    "med-gonal-f": 1451,
    "med-cetrotide": 302,
    "med-ovidrel": 107,
    "med-progesterone": 91,
    "med-dexamethasone": 5,
  },
};

/* ─── Savings Programs ───────────────────────────────────────────── */

export interface SavingsProgram {
  id: string;
  name: string;
  manufacturer: string;
  headline: string;
  description: string;
  eligibleMedIds: string[];
  maxSavingsPercent: number;
  estimatedSavings: number;
  eligibilityCriteria: string[];
  howItWorks: string[];
  applicationUrl: string;
}

export const MOCK_SAVINGS_PROGRAMS: SavingsProgram[] = [
  {
    id: "prog-compassionate-care",
    name: "Compassionate Care",
    manufacturer: "EMD Serono",
    headline: "Save up to 75% on Gonal-F & Cetrotide",
    description:
      "EMD Serono's Compassionate Care program provides significant discounts on fertility medications for qualifying patients based on household income.",
    eligibleMedIds: ["med-gonal-f", "med-cetrotide", "med-ovidrel"],
    maxSavingsPercent: 75,
    estimatedSavings: 810,
    eligibilityCriteria: [
      "Household income under $100,000/year",
      "No insurance coverage for fertility medications",
      "Valid prescription from a licensed provider",
      "US resident",
    ],
    howItWorks: [
      "Check your eligibility using the online tool",
      "Submit income verification documents",
      "Receive your discount card within 5-7 business days",
      "Present at any participating pharmacy for instant savings",
    ],
    applicationUrl: "https://www.fertilitylifelines.com",
  },
  {
    id: "prog-heartbeat",
    name: "HeartBeat Program",
    manufacturer: "Ferring Pharmaceuticals",
    headline: "Savings on Menopur & Endometrin",
    description:
      "Ferring's HeartBeat program offers discounted pricing on Menopur and Endometrin for first-time fertility patients and those without medication coverage.",
    eligibleMedIds: ["med-progesterone"],
    maxSavingsPercent: 40,
    estimatedSavings: 190,
    eligibilityCriteria: [
      "First-time IVF or IUI patient",
      "No prior use of Ferring fertility products",
      "Valid prescription from a reproductive endocrinologist",
      "US or Canadian resident",
    ],
    howItWorks: [
      "Register through your fertility clinic",
      "Your clinic verifies first-time patient status",
      "Receive program enrollment confirmation",
      "Discounted pricing applied automatically at participating pharmacies",
    ],
    applicationUrl: "https://www.ferringfertility.com",
  },
  {
    id: "prog-merck-savings",
    name: "Fertility Savings Program",
    manufacturer: "Merck",
    headline: "Copay assistance for Follistim & Ganirelix",
    description:
      "Merck's savings program helps reduce out-of-pocket costs for commercially insured patients using Follistim AQ and Ganirelix Acetate.",
    eligibleMedIds: [],
    maxSavingsPercent: 50,
    estimatedSavings: 0,
    eligibilityCriteria: [
      "Commercially insured (not government programs)",
      "Valid prescription for Follistim or Ganirelix",
      "US resident 18 years or older",
    ],
    howItWorks: [
      "Download the savings card from Merck's website",
      "Present at your pharmacy along with your insurance card",
      "Savings applied to your copay automatically",
      "Can be used for up to 12 fills per year",
    ],
    applicationUrl: "https://www.merckconnect.com",
  },
  {
    id: "prog-trumprx",
    name: "TrumpRx IVF Savings",
    manufacturer: "EMD Serono via TrumpRx",
    headline: "Up to 84% off Gonal-F, Ovidrel & Cetrotide",
    description:
      "Through the TrumpRx program, EMD Serono offers dramatically reduced pricing on fertility medications. Gonal-F pens are available for as low as $168 each — down from over $1,800. Available at participating pharmacies including Freedom Fertility and VFP Pharmacy Group.",
    eligibleMedIds: ["med-gonal-f", "med-cetrotide", "med-ovidrel"],
    maxSavingsPercent: 84,
    estimatedSavings: 2200,
    eligibilityCriteria: [
      "US resident",
      "Valid prescription from a licensed provider",
      "Must use a participating pharmacy",
      "No income requirements",
    ],
    howItWorks: [
      "Visit TrumpRx.gov and search for your medication",
      "Find a participating pharmacy near you",
      "Present the TrumpRx discount at the pharmacy",
      "Pay the reduced price — no insurance needed",
    ],
    applicationUrl: "https://www.trumprx.gov",
  },
  {
    id: "prog-ferring-greenlight",
    name: "IVF Greenlight",
    manufacturer: "Ferring Pharmaceuticals",
    headline: "Savings for cash-paying patients on Menopur & Endometrin",
    description:
      "Ferring's IVF Greenlight program provides savings on Menopur and Endometrin for patients paying out of pocket for fertility treatment.",
    eligibleMedIds: ["med-progesterone"],
    maxSavingsPercent: 30,
    estimatedSavings: 130,
    eligibilityCriteria: [
      "Cash-paying patient (no fertility medication coverage)",
      "Valid prescription for Ferring fertility products",
      "US resident",
    ],
    howItWorks: [
      "Ask your clinic about the IVF Greenlight program",
      "Complete enrollment through your fertility clinic",
      "Savings applied at participating pharmacies",
      "Can be combined with multi-cycle programs",
    ],
    applicationUrl: "https://www.ferringfertility.com/ivf-greenlight",
  },
  {
    id: "prog-compassionate-corps",
    name: "Compassionate Corps",
    manufacturer: "EMD Serono",
    headline: "Free fertility medications for qualifying veterans",
    description:
      "EMD Serono's Compassionate Corps provides fertility medications at no cost to veterans who were injured in the line of duty, or their spouses. This program covers Gonal-F, Ovidrel, and Cetrotide.",
    eligibleMedIds: ["med-gonal-f", "med-cetrotide", "med-ovidrel"],
    maxSavingsPercent: 100,
    estimatedSavings: 0,
    eligibilityCriteria: [
      "US military veteran injured in line of duty, or their spouse",
      "Service-connected injury documentation",
      "Valid prescription from a licensed provider",
    ],
    howItWorks: [
      "Contact Fertility LifeLines at 1-866-538-7879",
      "Provide proof of military service and injury documentation",
      "Receive enrollment confirmation",
      "Medications provided at no cost through participating pharmacies",
    ],
    applicationUrl: "https://www.fertilitylifelines.com",
  },
  {
    id: "prog-heartbeat-heroes",
    name: "Heart for Heroes",
    manufacturer: "Ferring Pharmaceuticals",
    headline: "Free medications for qualifying veterans",
    description:
      "Ferring's Heart for Heroes program provides Menopur and Endometrin at no cost to qualifying military veterans and their spouses pursuing fertility treatment.",
    eligibleMedIds: ["med-progesterone"],
    maxSavingsPercent: 100,
    estimatedSavings: 0,
    eligibilityCriteria: [
      "US military veteran or spouse",
      "Valid prescription from a reproductive endocrinologist",
      "Service-connected documentation",
    ],
    howItWorks: [
      "Contact Ferring at 1-800-515-3784",
      "Provide military service documentation",
      "Receive program enrollment",
      "Medications provided at no cost",
    ],
    applicationUrl: "https://www.ferringfertility.com/heartforheroes",
  },
  {
    id: "prog-costco-sesame",
    name: "Costco Fertility Savings",
    manufacturer: "Costco + Sesame + IVI RMA",
    headline: "Up to 80% off fertility medications for Costco members",
    description:
      "Through a partnership between Costco, Sesame, and IVI RMA, Costco members can access fertility medications at up to 80% off retail prices. Includes Follistim, Ganirelix, and other common fertility drugs.",
    eligibleMedIds: [],
    maxSavingsPercent: 80,
    estimatedSavings: 0,
    eligibilityCriteria: [
      "Active Costco membership",
      "Valid prescription from a licensed provider",
      "Available nationwide via Sesame",
    ],
    howItWorks: [
      "Visit sesamecare.com/fertility with your Costco membership",
      "Search for your prescribed medications",
      "Order through the Sesame platform",
      "Medications delivered to your door",
    ],
    applicationUrl: "https://sesamecare.com/fertility",
  },
];

/* ─── Delivery Tracking ──────────────────────────────────────────── */

export type DeliveryStep =
  | "placed"
  | "preparing"
  | "shipped"
  | "out_for_delivery"
  | "delivered";

export interface DeliveryOrder {
  id: string;
  pharmacyId: string;
  pharmacyName: string;
  pharmacyPhone: string;
  carrier: string;
  trackingNumber: string;
  estimatedDelivery: string;
  estimatedTime: string;
  currentStep: DeliveryStep;
  steps: { key: DeliveryStep; label: string; completedAt?: string }[];
  medications: {
    name: string;
    dosage: string;
    form: "pen" | "vial" | "pill" | "suppository";
    route: string;
  }[];
  totalPaid: number;
}

export const MOCK_ORDER: DeliveryOrder = {
  id: "order-001",
  pharmacyId: "pharmacy-alto",
  pharmacyName: "Alto Pharmacy",
  pharmacyPhone: "(415) 941-4286",
  carrier: "FedEx Priority Overnight",
  trackingNumber: "7489 2847 3910",
  estimatedDelivery: "March 16, 2026",
  estimatedTime: "by 3:00 PM",
  currentStep: "shipped",
  steps: [
    {
      key: "placed",
      label: "Order Placed",
      completedAt: "Mar 13, 10:24 AM",
    },
    {
      key: "preparing",
      label: "Pharmacy Preparing",
      completedAt: "Mar 14, 2:15 PM",
    },
    { key: "shipped", label: "Shipped", completedAt: "Mar 15, 8:30 AM" },
    { key: "out_for_delivery", label: "Out for Delivery" },
    { key: "delivered", label: "Delivered" },
  ],
  medications: [
    { name: "Gonal-F", dosage: "900 IU", form: "pen", route: "Subcutaneous" },
    {
      name: "Cetrotide",
      dosage: "0.25 mg",
      form: "vial",
      route: "Subcutaneous",
    },
    { name: "Ovidrel", dosage: "250 mcg", form: "pen", route: "Subcutaneous" },
    {
      name: "Endometrin",
      dosage: "100 mg",
      form: "suppository",
      route: "Vaginal",
    },
    { name: "Dexamethasone", dosage: "0.5 mg", form: "pill", route: "Oral" },
  ],
  totalPaid: 2290,
};

/* ─── Order Cost Breakdown ───────────────────────────────────────── */

export interface OrderCostBreakdown {
  subtotal: number;
  insuranceCovers: number;
  savingsDiscount: number;
  savingsProgramName: string;
  shipping: number;
  youPay: number;
  deliveryDate: string;
}

export const MOCK_COST_BREAKDOWN: OrderCostBreakdown = {
  subtotal: 4200,
  insuranceCovers: 1100,
  savingsDiscount: 810,
  savingsProgramName: "Compassionate Care",
  shipping: 0,
  youPay: 2290,
  deliveryDate: "March 16, 2026",
};

/* ─── Helpers ────────────────────────────────────────────────────── */

export const COVERAGE_CONFIG: Record<
  CoverageStatus,
  { label: string; color: string }
> = {
  covered: { label: "Covered", color: "#4E7342" },
  pa_approved: { label: "PA Approved", color: "#5E6E3E" },
  cash_pay: { label: "Cash Pay", color: "#B08030" },
};

export const PHARMACY_TYPE_LABELS: Record<PharmacyType, string> = {
  specialty: "Specialty",
  mail_order: "Mail Order",
  retail: "Retail",
};

export const FORM_LABELS: Record<MedForm, string> = {
  pen: "Pre-filled pen",
  vial: "Vial",
  tablet: "Tablet",
  suppository: "Suppository",
  syringe: "Pre-filled syringe",
};

/** Top pharmacies sorted by best total price (null-priced at end) */
export function getTopPharmacies(count = 3): Pharmacy[] {
  return [...MOCK_PHARMACIES]
    .sort((a, b) => {
      if (a.totalPrice == null && b.totalPrice == null) return 0;
      if (a.totalPrice == null) return 1;
      if (b.totalPrice == null) return -1;
      return a.totalPrice - b.totalPrice;
    })
    .slice(0, count);
}
